import React, { useEffect, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState } from "@/hq/HQPrimitives";
import CreateInvoiceModal from "@/hq/clients/CreateInvoiceModal";
import InvoiceEmailPreviewModal from "@/hq/clients/InvoiceEmailPreviewModal";
import { toast } from "sonner";
import { Plus, Mail, Copy, Send, XCircle, Loader2, CheckCircle2 } from "lucide-react";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => { if (!v) return "—"; try { return new Date(v).toLocaleDateString(); } catch { return "—"; } };

const STATUS_TONE = {
  DRAFT:          { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
  READY:          { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0" },
  SENT:           { fg: "#8A4A00", bg: "#FFF7EE", bd: "#FCE1C7" },
  PARTIALLY_PAID: { fg: "#7A5A00", bg: "#FFF3D0", bd: "#F5D08A" },
  PAID:           { fg: "#0FA958", bg: "#EBFBF2", bd: "#B7EBCF" },
  OVERDUE:        { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7" },
  CANCELLED:      { fg: "#4A4A4A", bg: "#F2F2F2", bd: "#EAEAEA" },
};

function StatusPill({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.DRAFT;
  return (
    <span
      data-testid={`inv-status-${status}`}
      className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.14em] uppercase"
      style={{ color: t.fg, background: t.bg, border: `1px solid ${t.bd}` }}
    >
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function paymentStructure(invoice) {
  const n = (invoice.installments || []).length;
  if (n <= 1) return "Pay in full";
  if (n === 2) return "50 / 50";
  if (n === 3) return "40 / 30 / 30";
  return `Custom · ${n} installments`;
}

function nextUnpaid(invoice) {
  return (invoice.installments || []).find((i) => !["PAID", "CANCELLED"].includes(i.status));
}

/**
 * ClientInvoicesTab — Phase 2C.
 *
 * Full invoice table for a client. Reuses Stage 6A endpoints
 * (`/api/hq/invoices/*`). Actions: preview + send next unpaid
 * installment, resend, copy payment link, cancel invoice, view.
 */
export default function ClientInvoicesTab({ businessId, contacts, primaryEmail, businessName, serviceSlots }) {
  const [items, setItems] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [previewInstallmentId, setPreviewInstallmentId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = async () => {
    try {
      const r = await hqApi.get("/invoices", { params: { business_id: businessId } });
      // Fetch each detail (installments) in parallel — the list endpoint
      // does not include them.
      const detailed = await Promise.all((r.data.items || []).map((inv) =>
        hqApi.get(`/invoices/${inv.id}`).then((d) => d.data).catch(() => inv)
      ));
      setItems(detailed);
    } catch {
      toast.error("Could not load invoices");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [businessId]);

  const markReadyIfDraft = async (inv) => {
    if (inv.status === "DRAFT") {
      try { await hqApi.post(`/invoices/${inv.id}/ready`); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not mark ready"); throw e; }
    }
  };

  const openSendPreview = async (inv) => {
    const nxt = nextUnpaid(inv);
    if (!nxt) return toast.message("All installments paid or cancelled");
    setBusyId(inv.id);
    try {
      await markReadyIfDraft(inv);
      setPreviewInstallmentId(nxt.id);
    } catch {
      // toast already shown
    } finally { setBusyId(null); }
  };

  const copyPaymentLink = async (inv) => {
    const nxt = nextUnpaid(inv);
    if (!nxt) return;
    setBusyId(inv.id);
    try {
      // Send/mint returns a fresh secure link with the raw token — safe
      // per Stage 6A architecture.
      await markReadyIfDraft(inv);
      const r = await hqApi.post(`/invoices/installments/${nxt.id}/send`);
      if (r.data.public_url) {
        await navigator.clipboard?.writeText(r.data.public_url);
        toast.success("Payment link copied");
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create payment link");
    } finally { setBusyId(null); }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel invoice ${inv.invoice_number}?`)) return;
    setBusyId(inv.id);
    try {
      await hqApi.post(`/invoices/${inv.id}/cancel`);
      toast.success("Invoice cancelled");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusyId(null); }
  };

  return (
    <Card className="p-6" data-testid="hq-client-invoices">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Invoices</div>
          <p className="text-sm text-[#4A4A4A] mt-1">Create, preview and send project invoices — payment status is webhook-authoritative.</p>
        </div>
        <button
          data-testid="hq-client-create-invoice"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.16em] uppercase hover:bg-[#e75f00]"
        >
          <Plus size={13}/> Create invoice
        </button>
      </div>

      {items === null ? (
        <div className="mt-8 flex items-center justify-center"><Loader2 size={18} className="animate-spin text-[#8A8A8A]"/></div>
      ) : items.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="No invoices yet" hint="Create an invoice with pay-in-full, 50/50, 40/30/30 or a custom schedule." testId="hq-client-invoices-empty"/>
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="hq-client-invoices-table">
            <thead className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#8A8A8A] bg-[#F6F5F2]">
              <tr>
                <th className="text-left px-3 py-3">Invoice</th>
                <th className="text-left px-3 py-3">Description</th>
                <th className="text-left px-3 py-3">Total</th>
                <th className="text-left px-3 py-3">Cur</th>
                <th className="text-left px-3 py-3">Structure</th>
                <th className="text-left px-3 py-3">Status</th>
                <th className="text-left px-3 py-3">Issued</th>
                <th className="text-left px-3 py-3">Due</th>
                <th className="text-left px-3 py-3">Paid</th>
                <th className="text-left px-3 py-3">Remaining</th>
                <th className="text-right px-3 py-3 w-56">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const busy = busyId === inv.id;
                const nxt = nextUnpaid(inv);
                const canSend = nxt && !["CANCELLED", "PAID"].includes(inv.status);
                return (
                  <tr key={inv.id} className="border-t border-[#EAEAEA]" data-testid={`hq-client-inv-row-${inv.invoice_number}`}>
                    <td className="px-3 py-3 font-black">{inv.invoice_number}</td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{inv.description || "—"}</td>
                    <td className="px-3 py-3 font-black">{money(inv.total_amount_minor, inv.currency)}</td>
                    <td className="px-3 py-3 text-[10px] tracking-[0.14em] uppercase">{inv.currency}</td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{paymentStructure(inv)}</td>
                    <td className="px-3 py-3"><StatusPill status={inv.status}/></td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{fmtDate(inv.issue_date || inv.created_at)}</td>
                    <td className="px-3 py-3 text-[#4A4A4A]">{fmtDate(inv.due_date)}</td>
                    <td className="px-3 py-3 text-[#0FA958] font-bold">{money(inv.amount_paid_minor, inv.currency)}</td>
                    <td className="px-3 py-3 text-[#8A4A00] font-bold">{money(inv.amount_outstanding_minor, inv.currency)}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {busy && <Loader2 size={13} className="animate-spin text-[#8A8A8A] mr-1"/>}
                        {canSend && (
                          <button
                            data-testid={`hq-client-inv-send-${inv.invoice_number}`}
                            onClick={() => openSendPreview(inv)}
                            disabled={busy}
                            className="p-1.5 rounded-full text-[#FF6A00] hover:bg-[#FFF7EE]"
                            title={inv.status === "DRAFT" ? "Preview & send first installment" :
                                   nxt.status === "SENT" || nxt.status === "VIEWED" ? "Preview & resend link" :
                                   "Preview & send next installment"}
                          >
                            <Mail size={14}/>
                          </button>
                        )}
                        {canSend && (
                          <button
                            data-testid={`hq-client-inv-copylink-${inv.invoice_number}`}
                            onClick={() => copyPaymentLink(inv)}
                            disabled={busy}
                            className="p-1.5 rounded-full text-[#4A4A4A] hover:bg-[#F2F2F2]"
                            title="Copy payment link (creates/rotates secure link)"
                          >
                            <Copy size={14}/>
                          </button>
                        )}
                        {!["CANCELLED", "PAID"].includes(inv.status) && (
                          <button
                            data-testid={`hq-client-inv-cancel-${inv.invoice_number}`}
                            onClick={() => cancelInvoice(inv)}
                            disabled={busy}
                            className="p-1.5 rounded-full text-[#C1350B] hover:bg-[#FFECEC] disabled:opacity-40"
                            title="Cancel invoice"
                          >
                            <XCircle size={14}/>
                          </button>
                        )}
                        {inv.status === "PAID" && (
                          <span className="text-[#0FA958]" title="Paid in full"><CheckCircle2 size={14}/></span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateInvoiceModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        businessId={businessId}
        contacts={contacts}
        primaryEmail={primaryEmail}
        businessName={businessName}
        serviceSlots={serviceSlots}
        onCreated={() => { setAddOpen(false); reload(); }}
      />
      <InvoiceEmailPreviewModal
        open={!!previewInstallmentId}
        onClose={() => { setPreviewInstallmentId(null); reload(); }}
        installmentId={previewInstallmentId}
        onSent={() => { setPreviewInstallmentId(null); reload(); }}
      />
    </Card>
  );
}
